import i18n from '../index';

/**
 * Date formatting utilities using the Intl.DateTimeFormat API.
 * Automatically uses the current i18n locale.
 */

export type DateFormatStyle = 'short' | 'medium' | 'long' | 'full';

interface DateFormatOptions {
  style?: DateFormatStyle;
  includeTime?: boolean;
  timeStyle?: DateFormatStyle;
}

const styleToOptions: Record<DateFormatStyle, Intl.DateTimeFormatOptions> = {
  short: { month: 'numeric', day: 'numeric', year: '2-digit' },
  medium: { month: 'short', day: 'numeric', year: 'numeric' },
  long: { month: 'long', day: 'numeric', year: 'numeric' },
  full: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
};

type TimeFormatStyle = DateFormatStyle | 'hour';

const timeStyleToOptions: Record<TimeFormatStyle, Intl.DateTimeFormatOptions> =
  {
    hour: { hour: 'numeric', hour12: true },
    short: { hour: 'numeric', minute: '2-digit', hour12: true },
    medium: {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    },
    long: {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
      hour12: true,
    },
    full: {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'long',
      hour12: true,
    },
  };

const getLocale = (): string => i18n.language || 'en';

const safeFormat = (
  date: Date,
  options: Intl.DateTimeFormatOptions,
): string => {
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const locale = getLocale();
  try {
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch {
    return new Intl.DateTimeFormat('en', options).format(date);
  }
};

const toDate = (date: Date | number | string): Date =>
  date instanceof Date ? date : new Date(date);

/**
 * Formats a date according to the current locale.
 *
 * @param date - The date to format (Date object, timestamp, or ISO string)
 * @param options - Formatting options
 * @returns Formatted date string
 *
 * @example
 * formatDate(new Date()) // "Jan 5, 2026"
 * formatDate(new Date(), { style: 'long' }) // "January 5, 2026"
 * formatDate(new Date(), { includeTime: true }) // "Jan 5, 2026, 2:30 PM"
 */
export const formatDate = (
  date: Date | number | string | null | undefined,
  options: DateFormatOptions = {},
): string => {
  if (date === null || date === undefined || date === '') {
    return '';
  }
  const {
    style = 'medium',
    includeTime = false,
    timeStyle = 'short',
  } = options;

  const dateObj = toDate(date);
  const formatOptions: Intl.DateTimeFormatOptions = {
    ...styleToOptions[style],
    ...(includeTime ? timeStyleToOptions[timeStyle] : {}),
  };

  return safeFormat(dateObj, formatOptions);
};

/**
 * Formats a time according to the current locale.
 *
 * @param date - The date/time to format
 * @param style - The time format style
 * @returns Formatted time string
 *
 * @example
 * formatTime(new Date()) // "2:30 PM"
 * formatTime(new Date(2000, 0, 1, 14, 0), 'hour') // "2 PM" (no minutes)
 */
export const formatTime = (
  date: Date | number | string | null | undefined,
  style: TimeFormatStyle = 'short',
): string => {
  if (date === null || date === undefined || date === '') {
    return '';
  }
  const dateObj = toDate(date);
  return safeFormat(dateObj, timeStyleToOptions[style]);
};

/**
 * Formats a date as month and day only.
 *
 * @example
 * formatMonthAndDay(new Date()) // "Jan 5"
 * formatMonthAndDay(new Date(), 'long') // "January 5"
 */
export const formatMonthAndDay = (
  date: Date | number | string,
  monthStyle: 'numeric' | 'short' | 'long' = 'short',
  dayStyle: 'numeric' | '2-digit' = 'numeric',
): string => {
  const dateObj = toDate(date);
  return safeFormat(dateObj, { month: monthStyle, day: dayStyle });
};

/**
 * Formats a date as month, day, and year.
 *
 * @example
 * formatMonthDayYear(new Date()) // "01/05/2026"
 * formatMonthDayYear(new Date(), 'short') // "Jan 5, 2026"
 * formatMonthDayYear('2024-01-15T00:00:00.000Z', '2-digit', '2-digit', 'numeric', 'UTC') // "01/15/2024"
 */
export const formatMonthDayYear = (
  date: Date | number | string,
  monthStyle: 'numeric' | '2-digit' | 'short' | 'long' = '2-digit',
  dayStyle: 'numeric' | '2-digit' = '2-digit',
  yearStyle: 'numeric' | '2-digit' = 'numeric',
  timeZone?: string,
): string => {
  const dateObj = toDate(date);
  return safeFormat(dateObj, {
    month: monthStyle,
    day: dayStyle,
    year: yearStyle,
    ...(timeZone ? { timeZone } : {}),
  });
};

/**
 * Short month + day + 12-hour time (no year, no seconds).
 * Used for chat/event timestamps; callers often apply `.toUpperCase()`.
 * For seconds, spread and add `second: '2-digit'` (see chat message list).
 */
export const DATE_TIME_SHORT_MONTH_DAY_12H: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

/**
 * Formats a date and time with customizable options.
 * Used by Admin components that need specific Intl.DateTimeFormatOptions.
 *
 * @example
 * formatDateTime(new Date(), { month: 'short', day: 'numeric', year: 'numeric' })
 * formatDateTime(new Date(), DATE_TIME_SHORT_MONTH_DAY_12H)
 */
export const formatDateTime = (
  date: Date | number | string,
  options: Intl.DateTimeFormatOptions,
): string => {
  const dateObj = toDate(date);
  return safeFormat(dateObj, options);
};

export { formatRelativeTime } from './relative-time';
export { formatDurationFromTimestamp } from './duration';

/**
 * Returns the number of days since a given date as a display string.
 * Uses i18n translations for "Today" / "Day" / "Days" labels.
 *
 * @example
 * daysDifferenceFromDate('2026-01-15') // "Today" or "1 Day" or "5 Days"
 */
export const daysDifferenceFromDate = (
  date: string | Date | null | undefined,
): string => {
  if (date === null || date === undefined) {
    return '';
  }
  const inputDate = toDate(date);
  if (Number.isNaN(inputDate.getTime())) {
    return '';
  }
  const today = new Date();
  const timeDifference = today.getTime() - inputDate.getTime();
  const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

  if (daysDifference <= 0) {
    return i18n.t('date:today', { defaultValue: 'Today' });
  }
  if (daysDifference === 1) {
    return i18n.t('date:dayCount_one', {
      count: 1,
      defaultValue: '1 Day',
    });
  }
  return i18n.t('date:dayCount_other', {
    count: daysDifference,
    defaultValue: '{{count}} Days',
  });
};

/**
 * Formats a date with numeric date and time (e.g. "1/15/2026, 9:30 AM").
 * Replaces the legacy formatISODateSlashandAMPM.
 */
export const formatDateNumericWithTime = (
  date: Date | number | string | null | undefined,
): string | undefined => {
  if (date === null || date === undefined || date === '') {
    return undefined;
  }
  const dateObj = toDate(date);
  return (
    safeFormat(dateObj, {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    }) || undefined
  );
};
